import {
    Button,
    CardActionArea,
    Collapse,
    FormLabel,
    Grid2,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    InputAdornment,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormHelperText
} from "@mui/material";
import axios from "axios"
import {useCallback, useContext, useEffect, useMemo, useState} from "react"
import {
    FormBody,
    RegistryControlContext,
    RegistryCurrentContext,
    RegistryDataContext,
    RegistryPropsContext,
    RegistryProvider,
    Service,
    useSource
} from "avrora"
import {
    Add,
    Delete,
    KeyboardArrowDown,
    KeyboardArrowRight,
    MoreVert,
    Remove,
    Visibility,
    VisibilityOff
} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {validateEmail} from "../../utils/email";
export const Domains = () => {
    const params = useParams();
    const navigate = useNavigate();
    const service = useMemo<Service<any>>(() => ({
        getList: params => axios.get(`/ecfg/api/domain?page=${(params.pagination.skip / params.pagination.top) + 1}&pageSize=${params.pagination.top}`).then(resp => ({
            count: resp.data.length,
            data: resp.data
        })),
        getItem: async i => {
            try {
                const fromApi = await axios.get(`/ecfg/api/domain/${i.id}`);
                return fromApi.data;
            } catch (e) {
                return axios.get(`/ecfg/api/domain`)
                    .then(resp => resp.data.find((j: any) => j.domainId?.toString() === i.id))
                    .catch(() => undefined);
            }
        },
        postItem: ({item}) => axios.post(`/ecfg/api/domain`, item).then(resp => resp.data),
        patchItem: async ({item}) => {
            return axios.put(`/ecfg/api/domain/${item.domainId}`, item).then(resp => resp.data)
        },
        putItem: async ({item}) => {
            return axios.put(`/ecfg/api/domain/${item.domainId}`, item).then(resp => resp.data)
        },
        removeItem: i => axios.delete(`/eus/domain/${i.id}`)
    }), []);
    return (
        <RegistryProvider
            action={params?.id === "create" ? "create" : undefined}
            id={params?.id !== "create" ? params.id : undefined}
            onOpenItem={id => navigate(`/page/domains/${id}`)}
            service={service}
        >
            <Page/>
        </RegistryProvider>
    )
}
export const Page = () => {
    const {data, item, save} = useContext(RegistryDataContext);
    const {id} = useContext(RegistryPropsContext);
    const {current, setCurrent} = useContext(RegistryCurrentContext);
    const {fetchList} = useContext(RegistryControlContext);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [authType, setAuthType] = useState<string>('basic');
    const [firstTypeCheck, setFirstTypeCheck] = useState<boolean>(false);
    const [isExternal, setIsExternal] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [initialData, setInitialData] = useState<any>(null);
    useEffect(() => {
        if (current && !initialData) {
            setInitialData(JSON.stringify(current));
        }
    }, [current, initialData]);
    const hasUnsavedChanges = useMemo(() => {
        if (!initialData || !current) return false;
        try {
            return initialData !== JSON.stringify(current);
        } catch {
            return false;
        }
    }, [current, initialData]);
    useEffect(() => {
        if (!item || !hasUnsavedChanges) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = 'Вы точно хотите покинуть страницу?';
            return e.returnValue;
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowConfirmClose(true);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [item, hasUnsavedChanges]);
    const resetStates = useCallback(() => {
        setAuthType('Basic');
        setFirstTypeCheck(false);
        setIsExternal(false);
        setInitialData(null);
        setDomainSettings(null);
    }, []);
    useEffect(() => {
        if (item?.domainId) {
            resetStates()
        }
    }, [item?.domainId]);
    const handleGoBack = () => {
        if (hasUnsavedChanges) {
            setShowConfirmClose(true);
        } else {
            navigate('/page/domains');
            resetStates();
        }
    };
    const handleConfirmClose = (confirm: boolean) => {
        setShowConfirmClose(false);
        if (confirm) {
            navigate('/page/domains');
            resetStates();
        }
    };
    useEffect(() => {
        if (current?.authSettings && id && !firstTypeCheck) {
            setFirstTypeCheck(true);
            if (current.authSettings.oauthParams || current.authSettings.ldapParams) {
                setAuthType('oidc');
            } else {
                setAuthType('basic');
            }
        }
    }, [current, id]);
    useEffect(() => {
        if (current?.external) {
            setAuthType('basic');
            setFirstTypeCheck(true);
        }
    }, [current?.external]);
    useEffect(() => {
        const root = data?.find(i => !i.parentDomain);
        if (root && current && current.domainId !== root.domainId && !current.parentDomain) {
            setCurrent({...current, parentDomain: root.domainId})
        }
    }, [data, current, setCurrent]);
    const {data: domainData, loading} = useSource(() => {
        if (!item || !item.domainId || !id) {
            return Promise.reject();
        }
        return axios.get(`/ecfg/api/domain/${id}`).then(resp => {
            resetStates();
            setIsExternal(resp.data.external === true);
            if (resp.data.external) {
                setAuthType('basic');
            }
            return axios.get(`/ecfg/api/domain/${id}/mail`).then(mailResp => ({
                ...mailResp.data,
                smtpServerProperties: mailResp.data.smtpServerProperties && Object.keys(mailResp.data.smtpServerProperties).reduce((coll, item) => [...coll, {
                    propertyName: item,
                    propertyValue: mailResp.data.smtpServerProperties[item]
                }] as any, [])
            }));
        });
    }, [item?.domainId, id]);
    const [domainSettings, setDomainSettings] = useState(domainData);
    useEffect(() => {
        setDomainSettings(domainData);
    }, [domainData]);
    useEffect(() => {
        if (!current) return;
        setCurrent((old: FormBody) => ({
            ...old,
            authSettings: {
                ...old.authSettings,
                basicAuthSettings: {
                    ...old.authSettings?.basicAuthSettings,
                    strongPasswordRequired: authType === 'basic'
                }
            }
        }));
    }, [authType]);
    const handleSave = async () => {
        if (id) {
            await axios.put(`/ecfg/api/domain/${id}/mail`, {
                ...domainSettings,
                smtpServerProperties: domainSettings.smtpServerProperties.reduce((coll: any, item: any) => ({
                    ...coll,
                    [item.propertyName]: item.propertyValue
                }), {})
            });
            if (authType === 'basic') {
                delete current?.authSettings?.oauthParams;
                delete current?.authSettings?.ldapParams;
                delete current?.authSettings?.basicAuthSettings
            }
        }
        await save(current);
        setInitialData(null);
        return navigate('/page/domains');
    }
    if (item) {
        return (
            <Stack
                maxWidth={{ xs: '100%', sm: 1024 }}
                minWidth={{ xs: 'auto', sm: 1024 }}
                padding={{ xs: '1rem', sm: '3rem' }}
                spacing={2}
                sx={{ width: '100%', boxSizing: 'border-box' }}
            >
                <Dialog
                    open={showConfirmClose}
                    onClose={() => handleConfirmClose(false)}
                >
                    <DialogTitle>Подтверждение</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Вы точно хотите покинуть страницу?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleConfirmClose(false)}>Отмена</Button>
                        <Button onClick={() => handleConfirmClose(true)} color="primary">
                            Да, закрыть
                        </Button>
                    </DialogActions>
                </Dialog>
                <Stack
                    maxWidth={{ xs: '100%', sm: 712 }}
                    minWidth={{ xs: 'auto', sm: 712 }}
                    padding={{ xs: '1.5rem', sm: '3rem' }}
                    spacing={{ xs: 3, sm: 4 }}
                    sx={{ width: '100%', boxSizing: 'border-box' }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h1">
                            {id === "external" ? (
                                <>Настройки внешних пользователей</>
                            ) : (
                                <>{!current || !current.parentDomain ? "Домен" : "Поддомен"} ID {item?.domainId}</>
                            )}
                        </Typography>
                        <Button variant="outlined" onClick={handleGoBack}>Назад</Button>
                    </Stack>
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center">
                            <TextField
                                size="small"
                                label="Название домена"
                                fullWidth
                                value={current?.domainName}
                                onChange={e => setCurrent((old: FormBody) => ({...old, domainName: e.target.value}))}
                                autoFocus
                            />
                        </Stack>
                        <Stack direction="row" alignItems="center">
                            <TextField
                                size="small"
                                label="Регулярное выражение"
                                fullWidth
                                value={current?.regex}
                                onChange={e => setCurrent((old: FormBody) => ({...old, regex: e.target.value}))}
                            />
                        </Stack>
                        <Stack spacing={2}>
                            <FormLabel>Почта поддержки</FormLabel>
                            {current?.supportMail?.map((i: string, j: number) => (
                                <Stack key={j} spacing={1} alignItems="center" direction="row">
                                    <Stack flexGrow={1}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={i}
                                            error={!!i && !validateEmail(i)}
                                            onChange={e => {
                                                const val = e.target.value.replace(/[:!?<>&]/g, '');
                                                const items = val.split(/[,;\s]/);
                                                setCurrent((old: FormBody) => {
                                                    return {
                                                        ...old,
                                                        supportMail: [...old.supportMail.slice(0, j), ...items, ...old.supportMail.slice(j + 1)]
                                                    }
                                                })
                                            }}
                                        />
                                    </Stack>
                                    <Stack>
                                        <IconButton size="small" onClick={() => setCurrent((old: FormBody) => ({
                                            ...old,
                                            supportMail: old.supportMail.filter((ii: string, jj: number) => jj !== j)
                                        }))}><Delete/></IconButton>
                                    </Stack>
                                </Stack>
                            ))}
                            <Stack direction="row">
                                <Button variant="text" onClick={() => setCurrent((old: FormBody) => ({
                                    ...old,
                                    supportMail: [...(old.supportMail ?? []), '']
                                }))} startIcon={<Add/>}>Добавить</Button>
                            </Stack>
                        </Stack>
                        <Stack direction="row" alignItems="center">
                            <TextField
                                size="small"
                                label="UI Адрес"
                                fullWidth
                                value={current?.uiAddr}
                                onChange={e => setCurrent((old: FormBody) => ({...old, uiAddr: e.target.value}))}
                            />
                        </Stack>
                        <FormControl fullWidth>
                            <InputLabel required={true}>Тип аутентификации</InputLabel>
                            <Select
                                value={authType}
                                label="Тип аутентификации"
                                disabled={isExternal}
                                onChange={(e) => setAuthType(e.target.value as string)}
                            >
                                <MenuItem value="basic">Basic</MenuItem>
                                <MenuItem value="oidc">OIDC</MenuItem>
                            </Select>
                        </FormControl>
                        {authType === 'basic' && (
                            <>
                            <div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={current?.manualConfirmationRequired}
                                            onChange={e => setCurrent((old: FormBody) => ({
                                                ...old,
                                                manualConfirmationRequired: e.target.checked
                                            }))}
                                        />
                                    }
                                    label="Требуется ли ручное подтверждение аккаунта оператором"
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={current?.authSettings?.twoFactorAuthenticationRequired}
                                            onChange={e => setCurrent((old: FormBody) => ({
                                                ...old,
                                                authSettings: {
                                                    ...old.authSettings,
                                                    twoFactorAuthenticationRequired: e.target.checked
                                                }
                                            }))}
                                        />
                                    }
                                    label="Требуется ли 2FA"
                                />
                            </div>
                            </>)
                        }
                        {authType === 'oidc' && (
                            <>
                                <div>
                                    <TextField
                                        size="small"
                                        label="Идентификатор учетной записи"
                                        fullWidth
                                        value={current?.authSettings?.oauthParams?.clientId || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                oauthParams: {
                                                    ...old.authSettings?.oauthParams,
                                                    clientId: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        ID клиента у провайдер OIDC (Client ID)
                                    </FormHelperText>
                                </div>
                                <div>
                                    <TextField
                                        size="small"
                                        label="Токен учетной записи"
                                        fullWidth
                                        value={current?.authSettings?.oauthParams?.clientSecret || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                oauthParams: {
                                                    ...old.authSettings?.oauthParams,
                                                    clientSecret: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Пароль клиента провайдера OIDC (Client Secret)
                                    </FormHelperText>
                                </div>
                                <div>
                                    <TextField
                                        size="small"
                                        label="URL сервера авторизации"
                                        fullWidth
                                        value={current?.authSettings?.oauthParams?.openidConnectUrl || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                oauthParams: {
                                                    ...old.authSettings?.oauthParams,
                                                    openidConnectUrl: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Например: https://login.test.ru/auth/realms/test1/protocol/openid-connect
                                    </FormHelperText>
                                </div>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={current?.authSettings?.oauthParams?.isClientServiceAccount ?? true}
                                            onChange={e => setCurrent((old: FormBody) => ({
                                                ...old,
                                                authSettings: {
                                                    ...old.authSettings,
                                                    oauthParams: {
                                                        ...old.authSettings?.oauthParams,
                                                        isClientServiceAccount: e.target.checked
                                                    }
                                                }
                                            }))}
                                        />
                                    }
                                    label="Клиент с ролью ServiceAccount"
                                />
                                <div>
                                    <TextField
                                        size="small"
                                        label="Cтрока поиска на ldap-сервере для фильтра по dc"
                                        fullWidth
                                        value={current?.authSettings?.ldapParams?.ldapSearchFilter || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                ldapParams: {
                                                    ...old.authSettings?.ldapParams,
                                                    ldapSearchFilter: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Например: (&(mail=)(objectclass=person)), если не задано, то будет использовано:
                                        (&(mail=*)(|(objectclass=person)(objectclass=organizationalPerson)(objectclass=user)(objectclass=inetOrgPerson)))
                                    </FormHelperText>
                                </div>
                                <div>
                                    <TextField
                                        size="small"
                                        label="Группа пользователей с доступом к системе в AD"
                                        fullWidth
                                        value={current?.authSettings?.ldapParams?.ldapFilterGroup || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                ldapParams: {
                                                    ...old.authSettings?.ldapParams,
                                                    ldapFilterGroup: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Например: OU=Users,OU=TEST1,DC=testlab1,DC=ext1
                                    </FormHelperText>
                                </div>
                                <div>
                                    <TextField
                                        size="small"
                                        label="База поиска (Base DN)"
                                        fullWidth
                                        value={current?.authSettings?.ldapParams?.ldapGroup || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                ldapParams: {
                                                    ...old.authSettings?.ldapParams,
                                                    ldapGroup: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Например: OU=Users,OU=TEST1,DC=testlab1,DC=ext1
                                    </FormHelperText>
                                </div>
                                <div>
                                    <TextField
                                        size="small"
                                        label="Логин технической учетной записи LDAP"
                                        fullWidth
                                        value={current?.authSettings?.ldapParams?.ldapUser || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                ldapParams: {
                                                    ...old.authSettings?.ldapParams,
                                                    ldapUser: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Например: CN=First1 Last1,OU=Users,OU=TEST1,DC=testlab1,DC=ext1
                                    </FormHelperText>
                                </div>
                                <TextField
                                    size="small"
                                    label="Пароль технической учетной записи LDAP"
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    value={current?.authSettings?.ldapParams?.ldapPassword || ''}
                                    onChange={e => setCurrent((old: FormBody) => ({
                                        ...old,
                                        authSettings: {
                                            ...old.authSettings,
                                            ldapParams: {
                                                ...old.authSettings?.ldapParams,
                                                ldapPassword: e.target.value
                                            }
                                        }
                                    }))}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <div>
                                    <TextField
                                        size="small"
                                        label="URL LDAP сервера"
                                        fullWidth
                                        value={current?.authSettings?.ldapParams?.ldapUrl || ''}
                                        onChange={e => setCurrent((old: FormBody) => ({
                                            ...old,
                                            authSettings: {
                                                ...old.authSettings,
                                                ldapParams: {
                                                    ...old.authSettings?.ldapParams,
                                                    ldapUrl: e.target.value
                                                }
                                            }
                                        }))}
                                    />
                                    <FormHelperText sx={{fontSize: '0.75rem', mt: 0.5, ml: 0.5}}>
                                        Например: ldap://dc1-lab2.testlab2.testlab1.ext1
                                    </FormHelperText>
                                </div>
                            </>
                        )}
                    </Stack>
                    {current?.domainId && domainSettings && !loading && (
                        <>
                            <Typography variant="h6">Настройки SMTP</Typography>
                            <Stack spacing={2}>
                                <TextField
                                    size="small"
                                    label="Логин"
                                    fullWidth
                                    value={domainSettings?.user ?? ''}
                                    onChange={e => setDomainSettings((old: FormBody) => ({
                                        ...old,
                                        user: e.target.value
                                    }))}
                                />
                                <TextField
                                    size="small"
                                    label="Пароль"
                                    type="password"
                                    fullWidth
                                    value={domainSettings?.password ?? ''}
                                    onChange={e => setDomainSettings((old: FormBody) => ({
                                        ...old,
                                        password: e.target.value
                                    }))}
                                />
                                <TextField
                                    size="small"
                                    label="Адрес SMTP"
                                    fullWidth
                                    value={domainSettings?.smtpServerAddress ?? ''}
                                    onChange={e => setDomainSettings((old: FormBody) => ({
                                        ...old,
                                        smtpServerAddress: e.target.value
                                    }))}
                                />
                                <TextField
                                    size="small"
                                    label="Алиас отправителя сообщений от имени шифропочты"
                                    fullWidth
                                    value={domainSettings?.senderAddressReplacement ?? ''}
                                    onChange={e => setDomainSettings((old: FormBody) => ({
                                        ...old,
                                        senderAddressReplacement: e.target.value
                                    }))}
                                />
                                {domainSettings?.smtpServerProperties?.map((i: any, j: number) => (
                                    <Stack key={j} direction="row" spacing={2}>
                                        <TextField
                                            size="small"
                                            label="Свойство"
                                            fullWidth
                                            value={i?.propertyName}
                                            onChange={e => setDomainSettings((old: FormBody) => ({
                                                ...old,
                                                smtpServerProperties: old.smtpServerProperties.map((ii: any, jj: number) => jj === j ? {
                                                    ...ii,
                                                    propertyName: e.target.value
                                                } : ii)
                                            }))}
                                        />
                                        <TextField
                                            size="small"
                                            label="Значение"
                                            fullWidth
                                            value={i?.propertyValue}
                                            onChange={e => setDomainSettings((old: FormBody) => ({
                                                ...old,
                                                smtpServerProperties: old.smtpServerProperties.map((ii: any, jj: number) => jj === j ? {
                                                    ...ii,
                                                    propertyValue: e.target.value
                                                } : ii)
                                            }))}
                                        />
                                        <IconButton onClick={e => setDomainSettings((old: FormBody) => ({
                                            ...old,
                                            smtpServerProperties: old.smtpServerProperties.filter((ii: any) => ii !== i)
                                        }))}><Remove/></IconButton>
                                    </Stack>
                                ))}
                                <Button onClick={e => setDomainSettings((old: FormBody) => ({
                                    ...old,
                                    smtpServerProperties: [...(old.smtpServerProperties ?? []), {
                                        propertyName: '',
                                        propertyValue: ''
                                    }]
                                }))} startIcon={<Add/>}>Добавить свойство</Button>
                            </Stack>
                        </>
                    )}
                    <Button
                        disabled={!authType || !current || !current.domainName || current.supportMail?.find((i: string) => !!i && !validateEmail(i)) || !current.regex}
                        onClick={() => {
                            handleSave().then(fetchList)
                        }}>Сохранить</Button>
                </Stack>
            </Stack>
        );
    }
    return (
        <Stack
            maxWidth={{ xs: '100%', sm: 1024 }}
            minWidth={{ xs: 'auto', sm: 1024 }}
            padding={{ xs: '1rem', sm: '3rem' }}
            spacing={2}
            sx={{ width: '100%', boxSizing: 'border-box' }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h1">Список доменов</Typography>
                {!data?.filter(i => !i.parentDomain).length && (
                    <Button startIcon={<Add/>} onClick={() => navigate("/page/domains/create")}>Создать</Button>
                )}
            </Stack>
            <List/>
        </Stack>
    )
}
const List = () => {
    const {data} = useContext(RegistryDataContext);
    return (
        <Stack>
            {data?.filter(i => !i.parentDomain).map((i: any) => (
                <DomainItem {...i} />
            ))}
        </Stack>
    )
}
export const DomainItem = ({domainId, domainName, regex, parentDomain, supportMail, uiAddr}: any) => {
    const navigate = useNavigate();
    const [opened, setOpened] = useState(true);
    const {data} = useContext(RegistryDataContext);
    return (
        <>
            <Stack borderRadius={6}>
                <CardActionArea onClick={() => navigate(`/page/domains/${domainId}`)} key={domainId}>
                    <Stack p={1} pl={2}>
                        <Grid2 container size={12}  spacing={{ xs: 1, sm: 1 }} alignItems="center">
                            <Grid2 size={{ xs: 12, sm: 1 }}>
                                {!!data?.filter(i => i.parentDomain === domainId).length && (
                                    <IconButton
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setOpened(val => !val)
                                        }}
                                    >
                                        {opened ? <KeyboardArrowDown/> : <KeyboardArrowRight/>}
                                    </IconButton>
                                )}
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 3 }}>
                                <Typography>{domainName}</Typography>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 4 }}>
                                <Typography>{supportMail.join(', ')}</Typography>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 3 }}>
                                <Typography>{uiAddr}</Typography>
                            </Grid2>
                            <Grid2 display="flex" justifyContent="flex-end" size={{ xs: 12, sm: 1 }}>
                                {!parentDomain && (
                                    <Actions onCreate={() => navigate('/page/domains/create')}/>
                                )}
                            </Grid2>
                            <Grid2 size={1}></Grid2>
                            <Grid2 size={{ xs: 12, sm: 11 }}>
                                <Typography variant="body2">{regex}</Typography>
                            </Grid2>
                        </Grid2>
                    </Stack>
                </CardActionArea>
            </Stack>
            <Collapse in={opened}>
                {data?.filter(i => i.parentDomain === domainId).map((i: any) => (
                    <Grid2 container size={12} spacing={1} alignItems="center">
                        <Grid2 size={1}></Grid2>
                        <Grid2 size={11}>
                            <DomainItem {...i} />
                        </Grid2>
                    </Grid2>
                ))}
            </Collapse>
        </>
    )
}
export const Actions = ({
                            onCreate,
                        }: {
    onCreate?: () => void,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.nativeEvent.stopImmediatePropagation();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (e: any) => {
        setAnchorEl(null);
        e.stopPropagation();
    };
    const handleCreate = (event: any) => {
        event.preventDefault();
        event.nativeEvent.stopImmediatePropagation();
        event.stopPropagation();
        handleClose(event);
        onCreate?.();
    }
    return (
        <div>
            <IconButton size="small" onClick={handleClick}><MoreVert/></IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {onCreate && <MenuItem onClick={handleCreate}>Создать поддомен</MenuItem>}
            </Menu>
        </div>
    )
}
